import { PrimitiveBlueprint } from "../types";
import { PRIMITIVE_COLORS } from "../../constants";

/**
 * Primitive icon blueprints for use in UI elements.
 * Each icon is designed at 32x32 and should scale cleanly from 0.5x to 2x.
 */

export const PRIMITIVE_ICONS = {
  attack: {
    id: "attack",
    designSize: { w: 32, h: 32 },
    parts: [
      // Blade
      {
        id: "blade",
        type: "rect" as const,
        bounds: { x: 0.4, y: 0.1, w: 0.2, h: 0.5 },
        style: { fill: PRIMITIVE_COLORS.lightBlue },
        z: 2,
      },
      // Blade tip
      {
        id: "tip",
        type: "triangle" as const,
        bounds: { x: 0.35, y: 0.05, w: 0.3, h: 0.15 },
        style: { fill: PRIMITIVE_COLORS.lightSky },
        z: 3,
      },
      // Guard
      {
        id: "guard",
        type: "rect" as const,
        bounds: { x: 0.25, y: 0.55, w: 0.5, h: 0.08 },
        style: { fill: PRIMITIVE_COLORS.amber },
        z: 1,
      },
      // Grip
      {
        id: "grip",
        type: "rect" as const,
        bounds: { x: 0.4, y: 0.6, w: 0.2, h: 0.3 },
        style: { fill: PRIMITIVE_COLORS.stone },
        z: 1,
      },
      // Pommel
      {
        id: "pommel",
        type: "circle" as const,
        bounds: { x: 0.35, y: 0.85, w: 0.3, h: 0.15 },
        style: { fill: PRIMITIVE_COLORS.amber },
        z: 1,
      },
    ],
  } satisfies PrimitiveBlueprint,

  defend: {
    id: "defend",
    designSize: { w: 32, h: 32 },
    parts: [
      // Shield body
      {
        id: "body",
        type: "roundRect" as const,
        bounds: { x: 0.15, y: 0.1, w: 0.7, h: 0.7 },
        style: { fill: PRIMITIVE_COLORS.blue, stroke: PRIMITIVE_COLORS.darkSlate, strokeWidth: 2 },
        z: 1,
      },
      // Shield point (bottom)
      {
        id: "point",
        type: "triangle" as const,
        bounds: { x: 0.3, y: 0.7, w: 0.4, h: 0.25 },
        style: { fill: PRIMITIVE_COLORS.blue, stroke: PRIMITIVE_COLORS.darkSlate, strokeWidth: 2 },
        z: 1,
      },
      // Boss (center decoration)
      {
        id: "boss",
        type: "circle" as const,
        bounds: { x: 0.35, y: 0.35, w: 0.3, h: 0.3 },
        style: { fill: PRIMITIVE_COLORS.amber, stroke: PRIMITIVE_COLORS.amber, strokeWidth: 1.5 },
        z: 2,
      },
    ],
  } satisfies PrimitiveBlueprint,

  health: {
    id: "health",
    designSize: { w: 32, h: 32 },
    parts: [
      // Left lobe
      {
        id: "left",
        type: "circle" as const,
        bounds: { x: 0.1, y: 0.15, w: 0.4, h: 0.4 },
        style: { fill: PRIMITIVE_COLORS.red },
        z: 1,
      },
      // Right lobe
      {
        id: "right",
        type: "circle" as const,
        bounds: { x: 0.5, y: 0.15, w: 0.4, h: 0.4 },
        style: { fill: PRIMITIVE_COLORS.red },
        z: 1,
      },
      // Large bottom oval tapering to point
      {
        id: "bottom",
        type: "ellipse" as const,
        bounds: { x: 0.2, y: 0.3, w: 0.6, h: 0.6 },
        style: { fill: PRIMITIVE_COLORS.red },
        z: 0,
      },
      // Highlight
      {
        id: "highlight",
        type: "ellipse" as const,
        bounds: { x: 0.25, y: 0.2, w: 0.25, h: 0.15 },
        style: { fill: PRIMITIVE_COLORS.lightRed, fillAlpha: 0.6 },
        z: 2,
      },
    ],
  } satisfies PrimitiveBlueprint,

  slash: {
    id: "slash",
    designSize: { w: 32, h: 32 },
    parts: [
      // First slash line
      {
        id: "slash1",
        type: "line" as const,
        bounds: { x: 0.1, y: 0.2, w: 0.6, h: 0.4 },
        style: { stroke: PRIMITIVE_COLORS.lightYellow, strokeWidth: 3, strokeAlpha: 0.9 },
        z: 1,
      },
      // Second slash line (parallel)
      {
        id: "slash2",
        type: "line" as const,
        bounds: { x: 0.25, y: 0.35, w: 0.6, h: 0.4 },
        style: { stroke: PRIMITIVE_COLORS.lightYellow, strokeWidth: 3, strokeAlpha: 0.9 },
        z: 1,
      },
      // Third slash line (crossing)
      {
        id: "slash3",
        type: "line" as const,
        bounds: { x: 0.75, y: 0.2, w: -0.5, h: 0.5 },
        style: { stroke: PRIMITIVE_COLORS.lightYellow, strokeWidth: 3, strokeAlpha: 0.9 },
        z: 1,
      },
    ],
  } satisfies PrimitiveBlueprint,

  armor: {
    id: "armor",
    designSize: { w: 32, h: 32 },
    parts: [
      // Chestplate body
      {
        id: "chest",
        type: "roundRect" as const,
        bounds: { x: 0.2, y: 0.25, w: 0.6, h: 0.6 },
        style: { fill: PRIMITIVE_COLORS.slate, stroke: PRIMITIVE_COLORS.darkSlate, strokeWidth: 2 },
        z: 1,
      },
      // Shoulder left
      {
        id: "shoulderL",
        type: "roundRect" as const,
        bounds: { x: 0.05, y: 0.15, w: 0.25, h: 0.3 },
        style: {
          fill: PRIMITIVE_COLORS.slate,
          stroke: PRIMITIVE_COLORS.darkSlate,
          strokeWidth: 1.5,
        },
        z: 1,
      },
      // Shoulder right
      {
        id: "shoulderR",
        type: "roundRect" as const,
        bounds: { x: 0.7, y: 0.15, w: 0.25, h: 0.3 },
        style: {
          fill: PRIMITIVE_COLORS.slate,
          stroke: PRIMITIVE_COLORS.darkSlate,
          strokeWidth: 1.5,
        },
        z: 1,
      },
      // Belt/bottom trim
      {
        id: "belt",
        type: "rect" as const,
        bounds: { x: 0.2, y: 0.8, w: 0.6, h: 0.1 },
        style: { fill: PRIMITIVE_COLORS.stone },
        z: 2,
      },
      // Center decoration
      {
        id: "decoration",
        type: "circle" as const,
        bounds: { x: 0.4, y: 0.45, w: 0.2, h: 0.2 },
        style: { fill: PRIMITIVE_COLORS.slate },
        z: 2,
      },
    ],
  } satisfies PrimitiveBlueprint,

  default: {
    id: "default",
    designSize: { w: 32, h: 32 },
    parts: [
      // Star burst - center circle
      {
        id: "center",
        type: "circle" as const,
        bounds: { x: 0.35, y: 0.35, w: 0.3, h: 0.3 },
        style: { fill: PRIMITIVE_COLORS.yellow },
        z: 2,
      },
      // Four points (using rotated triangles would be complex, so use diamonds/rects)
      {
        id: "point-n",
        type: "triangle" as const,
        bounds: { x: 0.4, y: 0.05, w: 0.2, h: 0.3 },
        style: { fill: PRIMITIVE_COLORS.yellow },
        z: 1,
      },
      {
        id: "point-s",
        type: "triangle" as const,
        bounds: { x: 0.4, y: 0.65, w: 0.2, h: 0.3 },
        style: { fill: PRIMITIVE_COLORS.yellow },
        z: 1,
        rotation: Math.PI,
      },
      {
        id: "point-w",
        type: "triangle" as const,
        bounds: { x: 0.05, y: 0.4, w: 0.3, h: 0.2 },
        style: { fill: PRIMITIVE_COLORS.yellow },
        z: 1,
        rotation: -Math.PI / 2,
      },
      {
        id: "point-e",
        type: "triangle" as const,
        bounds: { x: 0.65, y: 0.4, w: 0.3, h: 0.2 },
        style: { fill: PRIMITIVE_COLORS.yellow },
        z: 1,
        rotation: Math.PI / 2,
      },
    ],
  } satisfies PrimitiveBlueprint,

  // Perk icons
  hp: {
    id: "hp",
    designSize: { w: 32, h: 32 },
    parts: [
      // Heart shape - left lobe
      {
        id: "left",
        type: "circle" as const,
        bounds: { x: 0.15, y: 0.2, w: 0.3, h: 0.3 },
        style: { fill: PRIMITIVE_COLORS.red },
        z: 1,
      },
      // Heart shape - right lobe
      {
        id: "right",
        type: "circle" as const,
        bounds: { x: 0.55, y: 0.2, w: 0.3, h: 0.3 },
        style: { fill: PRIMITIVE_COLORS.red },
        z: 1,
      },
      // Large bottom oval tapering to point
      {
        id: "bottom",
        type: "ellipse" as const,
        bounds: { x: 0.23, y: 0.33, w: 0.54, h: 0.55 },
        style: { fill: PRIMITIVE_COLORS.red },
        z: 0,
      },
      // Plus symbol overlay
      {
        id: "plus-h",
        type: "rect" as const,
        bounds: { x: 0.35, y: 0.45, w: 0.3, h: 0.1 },
        style: { fill: PRIMITIVE_COLORS.white },
        z: 2,
      },
      {
        id: "plus-v",
        type: "rect" as const,
        bounds: { x: 0.45, y: 0.35, w: 0.1, h: 0.3 },
        style: { fill: PRIMITIVE_COLORS.white },
        z: 2,
      },
    ],
  } satisfies PrimitiveBlueprint,

  atk: {
    id: "atk",
    designSize: { w: 32, h: 32 },
    parts: [
      // Sword (simplified, similar to attack but distinct)
      {
        id: "blade",
        type: "rect" as const,
        bounds: { x: 0.4, y: 0.15, w: 0.2, h: 0.45 },
        style: { fill: PRIMITIVE_COLORS.slate },
        z: 2,
      },
      {
        id: "tip",
        type: "triangle" as const,
        bounds: { x: 0.35, y: 0.1, w: 0.3, h: 0.12 },
        style: { fill: PRIMITIVE_COLORS.gray },
        z: 3,
      },
      {
        id: "guard",
        type: "rect" as const,
        bounds: { x: 0.25, y: 0.55, w: 0.5, h: 0.08 },
        style: { fill: PRIMITIVE_COLORS.amber },
        z: 1,
      },
      {
        id: "grip",
        type: "rect" as const,
        bounds: { x: 0.4, y: 0.6, w: 0.2, h: 0.25 },
        style: { fill: PRIMITIVE_COLORS.stone },
        z: 1,
      },
    ],
  } satisfies PrimitiveBlueprint,

  lifesteal: {
    id: "lifesteal",
    designSize: { w: 32, h: 32 },
    parts: [
      // Heart base - left lobe
      {
        id: "left",
        type: "circle" as const,
        bounds: { x: 0.15, y: 0.2, w: 0.3, h: 0.3 },
        style: { fill: PRIMITIVE_COLORS.red },
        z: 1,
      },
      // Heart base - right lobe
      {
        id: "right",
        type: "circle" as const,
        bounds: { x: 0.55, y: 0.2, w: 0.3, h: 0.3 },
        style: { fill: PRIMITIVE_COLORS.red },
        z: 1,
      },
      // Large bottom oval tapering to point
      {
        id: "bottom",
        type: "ellipse" as const,
        bounds: { x: 0.23, y: 0.33, w: 0.54, h: 0.55 },
        style: { fill: PRIMITIVE_COLORS.red },
        z: 0,
      },
      // Droplet overlay
      {
        id: "drop",
        type: "ellipse" as const,
        bounds: { x: 0.5, y: 0.5, w: 0.15, h: 0.2 },
        style: { fill: PRIMITIVE_COLORS.rose },
        z: 2,
      },
      {
        id: "drop-tip",
        type: "triangle" as const,
        bounds: { x: 0.48, y: 0.65, w: 0.19, h: 0.15 },
        style: { fill: PRIMITIVE_COLORS.rose },
        z: 2,
      },
    ],
  } satisfies PrimitiveBlueprint,

  thorns: {
    id: "thorns",
    designSize: { w: 32, h: 32 },
    parts: [
      // Central circle
      {
        id: "center",
        type: "circle" as const,
        bounds: { x: 0.35, y: 0.35, w: 0.3, h: 0.3 },
        style: { fill: PRIMITIVE_COLORS.green },
        z: 1,
      },
      // Spikes pointing outward
      {
        id: "spike-n",
        type: "triangle" as const,
        bounds: { x: 0.4, y: 0.05, w: 0.2, h: 0.25 },
        style: { fill: PRIMITIVE_COLORS.emerald },
        z: 2,
      },
      {
        id: "spike-s",
        type: "triangle" as const,
        bounds: { x: 0.4, y: 0.7, w: 0.2, h: 0.25 },
        style: { fill: PRIMITIVE_COLORS.emerald },
        z: 2,
        rotation: Math.PI,
      },
      {
        id: "spike-w",
        type: "triangle" as const,
        bounds: { x: 0.05, y: 0.4, w: 0.25, h: 0.2 },
        style: { fill: PRIMITIVE_COLORS.emerald },
        z: 2,
        rotation: -Math.PI / 2,
      },
      {
        id: "spike-e",
        type: "triangle" as const,
        bounds: { x: 0.7, y: 0.4, w: 0.25, h: 0.2 },
        style: { fill: PRIMITIVE_COLORS.emerald },
        z: 2,
        rotation: Math.PI / 2,
      },
    ],
  } satisfies PrimitiveBlueprint,

  rage: {
    id: "rage",
    designSize: { w: 32, h: 32 },
    parts: [
      // Fire base
      {
        id: "flame-base",
        type: "ellipse" as const,
        bounds: { x: 0.3, y: 0.5, w: 0.4, h: 0.3 },
        style: { fill: PRIMITIVE_COLORS.orange },
        z: 1,
      },
      // Flame tongues
      {
        id: "flame-left",
        type: "triangle" as const,
        bounds: { x: 0.25, y: 0.3, w: 0.2, h: 0.35 },
        style: { fill: PRIMITIVE_COLORS.red },
        z: 2,
      },
      {
        id: "flame-center",
        type: "triangle" as const,
        bounds: { x: 0.35, y: 0.2, w: 0.3, h: 0.4 },
        style: { fill: PRIMITIVE_COLORS.orange },
        z: 2,
      },
      {
        id: "flame-right",
        type: "triangle" as const,
        bounds: { x: 0.55, y: 0.3, w: 0.2, h: 0.35 },
        style: { fill: PRIMITIVE_COLORS.red },
        z: 2,
      },
      // Inner highlight
      {
        id: "highlight",
        type: "ellipse" as const,
        bounds: { x: 0.4, y: 0.35, w: 0.2, h: 0.25 },
        style: { fill: PRIMITIVE_COLORS.yellow, fillAlpha: 0.7 },
        z: 3,
      },
    ],
  } satisfies PrimitiveBlueprint,

  adrenaline: {
    id: "adrenaline",
    designSize: { w: 32, h: 32 },
    parts: [
      // Lightning bolt
      {
        id: "bolt-main",
        type: "rect" as const,
        bounds: { x: 0.42, y: 0.15, w: 0.16, h: 0.5 },
        style: { fill: PRIMITIVE_COLORS.yellow },
        z: 2,
        rotation: -0.2,
      },
      {
        id: "bolt-top",
        type: "triangle" as const,
        bounds: { x: 0.35, y: 0.1, w: 0.3, h: 0.2 },
        style: { fill: PRIMITIVE_COLORS.yellow },
        z: 2,
        rotation: -0.3,
      },
      {
        id: "bolt-bottom",
        type: "triangle" as const,
        bounds: { x: 0.4, y: 0.6, w: 0.2, h: 0.25 },
        style: { fill: PRIMITIVE_COLORS.yellow },
        z: 2,
        rotation: 0.2,
      },
      // Energy glow
      {
        id: "glow",
        type: "ellipse" as const,
        bounds: { x: 0.35, y: 0.35, w: 0.3, h: 0.3 },
        style: { fill: PRIMITIVE_COLORS.amber, fillAlpha: 0.4 },
        z: 1,
      },
    ],
  } satisfies PrimitiveBlueprint,

  // Future-use icons
  magic: {
    id: "magic",
    designSize: { w: 32, h: 32 },
    parts: [
      // Star shape
      {
        id: "star-center",
        type: "circle" as const,
        bounds: { x: 0.4, y: 0.4, w: 0.2, h: 0.2 },
        style: { fill: PRIMITIVE_COLORS.purple },
        z: 2,
      },
      // Sparkle points
      {
        id: "sparkle-n",
        type: "triangle" as const,
        bounds: { x: 0.42, y: 0.1, w: 0.16, h: 0.25 },
        style: { fill: PRIMITIVE_COLORS.violet },
        z: 1,
      },
      {
        id: "sparkle-s",
        type: "triangle" as const,
        bounds: { x: 0.42, y: 0.65, w: 0.16, h: 0.25 },
        style: { fill: PRIMITIVE_COLORS.violet },
        z: 1,
        rotation: Math.PI,
      },
      {
        id: "sparkle-w",
        type: "triangle" as const,
        bounds: { x: 0.1, y: 0.42, w: 0.25, h: 0.16 },
        style: { fill: PRIMITIVE_COLORS.violet },
        z: 1,
        rotation: -Math.PI / 2,
      },
      {
        id: "sparkle-e",
        type: "triangle" as const,
        bounds: { x: 0.65, y: 0.42, w: 0.25, h: 0.16 },
        style: { fill: PRIMITIVE_COLORS.violet },
        z: 1,
        rotation: Math.PI / 2,
      },
      // Diagonal sparkles
      {
        id: "sparkle-ne",
        type: "ellipse" as const,
        bounds: { x: 0.6, y: 0.2, w: 0.15, h: 0.15 },
        style: { fill: PRIMITIVE_COLORS.pink, fillAlpha: 0.7 },
        z: 1,
      },
      {
        id: "sparkle-sw",
        type: "ellipse" as const,
        bounds: { x: 0.25, y: 0.65, w: 0.15, h: 0.15 },
        style: { fill: PRIMITIVE_COLORS.pink, fillAlpha: 0.7 },
        z: 1,
      },
    ],
  } satisfies PrimitiveBlueprint,

  speed: {
    id: "speed",
    designSize: { w: 32, h: 32 },
    parts: [
      // Arrow body
      {
        id: "arrow-body",
        type: "rect" as const,
        bounds: { x: 0.2, y: 0.4, w: 0.4, h: 0.2 },
        style: { fill: PRIMITIVE_COLORS.cyan },
        z: 1,
        rotation: -0.3,
      },
      // Arrow head
      {
        id: "arrow-head",
        type: "triangle" as const,
        bounds: { x: 0.55, y: 0.35, w: 0.25, h: 0.3 },
        style: { fill: PRIMITIVE_COLORS.cyan },
        z: 2,
        rotation: -0.3,
      },
      // Motion lines
      {
        id: "line1",
        type: "line" as const,
        bounds: { x: 0.15, y: 0.3, w: 0.2, h: 0.1 },
        style: { stroke: PRIMITIVE_COLORS.sky, strokeWidth: 2, strokeAlpha: 0.6 },
        z: 0,
      },
      {
        id: "line2",
        type: "line" as const,
        bounds: { x: 0.15, y: 0.6, w: 0.2, h: 0.1 },
        style: { stroke: PRIMITIVE_COLORS.sky, strokeWidth: 2, strokeAlpha: 0.6 },
        z: 0,
      },
    ],
  } satisfies PrimitiveBlueprint,

  poison: {
    id: "poison",
    designSize: { w: 32, h: 32 },
    parts: [
      // Droplet base
      {
        id: "drop-base",
        type: "ellipse" as const,
        bounds: { x: 0.35, y: 0.35, w: 0.3, h: 0.4 },
        style: { fill: PRIMITIVE_COLORS.emerald },
        z: 1,
      },
      {
        id: "drop-tip",
        type: "triangle" as const,
        bounds: { x: 0.38, y: 0.65, w: 0.24, h: 0.2 },
        style: { fill: PRIMITIVE_COLORS.emerald },
        z: 1,
      },
      // Skull symbol overlay
      {
        id: "skull-top",
        type: "circle" as const,
        bounds: { x: 0.4, y: 0.4, w: 0.2, h: 0.2 },
        style: { fill: PRIMITIVE_COLORS.darkSlate },
        z: 2,
      },
      {
        id: "skull-eye-l",
        type: "circle" as const,
        bounds: { x: 0.42, y: 0.42, w: 0.05, h: 0.05 },
        style: { fill: PRIMITIVE_COLORS.white },
        z: 3,
      },
      {
        id: "skull-eye-r",
        type: "circle" as const,
        bounds: { x: 0.53, y: 0.42, w: 0.05, h: 0.05 },
        style: { fill: PRIMITIVE_COLORS.white },
        z: 3,
      },
      {
        id: "skull-mouth",
        type: "rect" as const,
        bounds: { x: 0.45, y: 0.5, w: 0.1, h: 0.05 },
        style: { fill: PRIMITIVE_COLORS.white },
        z: 3,
      },
    ],
  } satisfies PrimitiveBlueprint,

  chest: {
    id: "chest",
    designSize: { w: 32, h: 32 },
    parts: [
      // Chest body (main box)
      {
        id: "body",
        type: "roundRect" as const,
        bounds: { x: 0.15, y: 0.4, w: 0.7, h: 0.5 },
        style: { fill: PRIMITIVE_COLORS.amber, stroke: PRIMITIVE_COLORS.stone, strokeWidth: 2 },
        z: 1,
      },
      // Chest lid (top)
      {
        id: "lid",
        type: "roundRect" as const,
        bounds: { x: 0.15, y: 0.25, w: 0.7, h: 0.2 },
        style: { fill: PRIMITIVE_COLORS.yellow, stroke: PRIMITIVE_COLORS.stone, strokeWidth: 2 },
        z: 2,
      },
      // Decorative band across lid
      {
        id: "band",
        type: "rect" as const,
        bounds: { x: 0.2, y: 0.3, w: 0.6, h: 0.08 },
        style: { fill: PRIMITIVE_COLORS.stone },
        z: 3,
      },
      // Lock/keyhole (center decoration)
      {
        id: "lock",
        type: "circle" as const,
        bounds: { x: 0.4, y: 0.34, w: 0.2, h: 0.2 },
        style: {
          fill: PRIMITIVE_COLORS.slate,
          stroke: PRIMITIVE_COLORS.darkSlate,
          strokeWidth: 1.5,
        },
        z: 4,
      },
      // Keyhole detail
      {
        id: "keyhole",
        type: "rect" as const,
        bounds: { x: 0.47, y: 0.36, w: 0.06, h: 0.08 },
        style: { fill: PRIMITIVE_COLORS.darkSlate },
        z: 5,
      },
      // Highlight on lid (shine effect)
      {
        id: "highlight",
        type: "ellipse" as const,
        bounds: { x: 0.3, y: 0.28, w: 0.25, h: 0.12 },
        style: { fill: PRIMITIVE_COLORS.lightYellow, fillAlpha: 0.5 },
        z: 3,
      },
    ],
  } satisfies PrimitiveBlueprint,
} as const;

export type PrimitiveIconId = keyof typeof PRIMITIVE_ICONS;

/**
 * Get a primitive icon blueprint by ID.
 * Returns the default icon if the requested ID doesn't exist.
 */
export function getPrimitiveIcon(id: string): PrimitiveBlueprint {
  return PRIMITIVE_ICONS[id as PrimitiveIconId] ?? PRIMITIVE_ICONS.default;
}
